/*
 * Amazon-style product personalization — buyer customizer for the Horizon theme.
 * Self-contained canvas engine (no Fabric): config-driven image/text/option
 * components, drag/zoom/rotate free placement, clip to print area, conditional
 * reveals, live pricing. On "Save design" it writes the buyer's result into the
 * product form as line-item properties and reveals Add to cart / Buy it now.
 *
 * Adapted from the standalone Phase-1 customizer. Scoped to a `.perso` root so it
 * can run inside the product page without id collisions.
 */
(function () {
  "use strict";

  const FONT_FALLBACK = {
    "Great Vibes": "cursive", "Damion": "cursive",
    "Bebas Neue": "'Arial Narrow',sans-serif", "Montserrat": "system-ui,sans-serif"
  };
  // Render canvas text in the REAL chosen font (quoted family + generic fallback).
  const fontStack = (name) => name ? `"${name}", ${FONT_FALLBACK[name] || "sans-serif"}` : "sans-serif";
  // Load exactly the fonts a product offers, so every option the buyer sees renders correctly.
  const persoLoadFonts = (cfg, onReady) => {
    try {
      const fams = new Set();
      (function walk(o) { if (!o || typeof o !== "object") return; if (o.family) fams.add(o.family); for (const k in o) walk(o[k]); })(cfg);
      if (!fams.size) return;
      const href = "https://fonts.googleapis.com/css2?" + [...fams].map((f) => "family=" + encodeURIComponent(f).replace(/%20/g, "+")).join("&") + "&display=swap";
      const l = document.createElement("link"); l.rel = "stylesheet"; l.href = href;
      l.onload = () => { if (document.fonts && document.fonts.ready) document.fonts.ready.then(onReady); };
      document.head.appendChild(l);
    } catch (e) {}
  };
  const money = (n, cur) => (cur === "INR" ? "₹" : "") + Number(n).toLocaleString("en-IN");
  const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
  const ang = (a, b) => Math.atan2(b.y - a.y, b.x - a.x);
  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
  const round = (n, p = 2) => Math.round(n * 10 ** p) / 10 ** p;
  const isMobile = () => window.matchMedia("(max-width: 768px)").matches;

  function init(root) {
    if (root.dataset.persoReady) return;
    root.dataset.persoReady = "1";

    let RAWCFG;
    try {
      RAWCFG = JSON.parse(root.querySelector("[data-perso-config]").textContent);
    } catch (e) {
      console.error("[personalization] invalid config JSON", e);
      return;
    }

    const $ = (sel) => root.querySelector(sel);
    const canvas = $("[data-perso-canvas]");
    const ctx = canvas.getContext("2d");
    const controlsEl = $("[data-perso-controls]");
    const modal = $("[data-perso-modal]");
    const gate = root.closest("[data-perso-gate]") || root;   // .product-form-buttons wrapper
    const CUR = RAWCFG.currency || "INR";

    // ---- Shopify variant data (drives BOTH pricing and per-variant config resolution) ----
    let VARIANTS = null, OPTION_NAMES = [];
    try {
      const vEl = root.querySelector("[data-perso-variants]");
      if (vEl) { const vd = JSON.parse(vEl.textContent); VARIANTS = vd.variants; OPTION_NAMES = vd.optionNames || []; }
    } catch (e) { console.warn("[personalization] variant data parse failed", e); }
    const persoForm = root.closest("form");
    const formIdInput = persoForm ? persoForm.querySelector('input[name="id"]') : null;

    // ---- Per-variant config resolution (Slice 3) --------------------------------
    // The metafield config is EITHER one flat config { ...surfaces } (legacy) OR a
    // variant-keyed map:
    //   { keyBy:["Shape"], default:"<key>", map:{ "<joined keyBy option values>": {surfaces:[...]} }, ...meta }
    // The active config is chosen from the currently-selected Shopify variant's option
    // values — only the `keyBy` option(s) form the key — and re-resolved on variant change.
    const isMapped = () => !!(RAWCFG && RAWCFG.map && typeof RAWCFG.map === "object");
    function variantOptions(id) {
      if (!VARIANTS || !VARIANTS.length) return {};
      const v = VARIANTS.find((x) => String(x.id) === String(id)) || VARIANTS[0];
      return v ? v.options : {};
    }
    function currentVariantId() {
      if (formIdInput && formIdInput.value) return formIdInput.value;
      return VARIANTS && VARIANTS[0] ? VARIANTS[0].id : null;
    }
    function configKey(id) {
      if (!isMapped()) return null;
      const keyBy = RAWCFG.keyBy && RAWCFG.keyBy.length ? RAWCFG.keyBy : OPTION_NAMES;
      const opts = variantOptions(id);
      return keyBy.map((n) => opts[n]).join(" / ");
    }
    function resolveConfig(id) {
      if (!isMapped()) return RAWCFG;                       // legacy flat config
      const key = configKey(id);
      const entry = (key != null && RAWCFG.map[key]) || RAWCFG.map[RAWCFG.default] || RAWCFG.map[Object.keys(RAWCFG.map)[0]] || {};
      const meta = {};
      for (const k in RAWCFG) if (k !== "map" && k !== "keyBy" && k !== "default") meta[k] = RAWCFG[k];
      return Object.assign(meta, entry);
    }
    let activeKey = configKey(currentVariantId());
    let CONFIG = resolveConfig(currentVariantId());

    // Capture modal-internal element refs BEFORE relocating the modal — after the move,
    // root.querySelector can no longer reach them.
    const surfacesEl = modal.querySelector("[data-perso-surfaces]");
    const surfLabelEl = modal.querySelector("[data-perso-surflabel]");
    const priceEl = modal.querySelector("[data-perso-price]");
    const priceBreakEl = modal.querySelector("[data-perso-pricebreak]");
    const saveBtn = modal.querySelector("[data-perso-save]");
    const resetBtn = modal.querySelector("[data-perso-reset]");
    const centerBtn = modal.querySelector("[data-perso-center]");
    const toolsEl = modal.querySelector(".perso-stage__tools");
    const closeBtns = modal.querySelectorAll("[data-perso-close]");

    // Detach the modal to <body> so `position:fixed` can't be clipped by an ancestor
    // transform/overflow. The property inputs (.perso-props) stay inside the form.
    document.body.appendChild(modal);

    // Mobile "scroll down for details" cue — fades once the shopper scrolls the controls.
    const scrollCueEl = modal.querySelector("[data-perso-scrollcue]");
    const modalBodyEl = modal.querySelector(".perso-modal__body");
    if (scrollCueEl && modalBodyEl) modalBodyEl.addEventListener("scroll", () => { if (modalBodyEl.scrollTop > 8) scrollCueEl.classList.add("perso-hide"); }, { passive: true });

    // ---- Variant-backed pricing ----
    // A price-bearing option is "variant-backed" when its config carries `variantOption`
    // (a Shopify option name) and each choice carries `variantValue`. When present, the
    // charged price comes from the matched Shopify variant (not config priceDeltas), and
    // the customizer sets the product form's variant id to that variant.
    // (VARIANTS / OPTION_NAMES / formIdInput are parsed above, for config resolution.)
    const variantBackedOptions = () => allComps().filter((c) => c.type === "option" && c.variantOption);
    const isVariantPriced = () => !!(VARIANTS && VARIANTS.length && variantBackedOptions().length);
    function baseVariant() {
      if (!VARIANTS) return null;
      const id = formIdInput && formIdInput.value;
      return VARIANTS.find((v) => String(v.id) === String(id)) || VARIANTS[0];
    }
    // The current size's cheapest variant (its "from" / base price) — the no-add-on combo.
    // Computed independently of the form's selected variant id (which we mutate as options
    // resolve), so the base + add-on math never double-counts.
    function sizeBaseVariant() {
      if (!VARIANTS || !VARIANTS.length) return null;
      const bv = baseVariant();
      const keyBy = (RAWCFG.keyBy && RAWCFG.keyBy.length) ? RAWCFG.keyBy : OPTION_NAMES;
      const pool = bv ? VARIANTS.filter((v) => keyBy.every((n) => String(v.options[n]) === String(bv.options[n]))) : [];
      const use = pool.length ? pool : VARIANTS;
      return use.reduce((a, b) => (b.price < a.price ? b : a), use[0]);
    }
    // Merge the PDP-selected variant's option values with the customizer's variant-backed
    // selections, then find the matching variant. Returns null until all are chosen.
    function resolveVariant() {
      if (!isVariantPriced()) return null;
      const base = baseVariant();
      const target = Object.assign({}, base ? base.options : {});
      for (const c of variantBackedOptions()) {
        if (!isVisible(c)) continue;   // skip hidden options (e.g. the non-selected orientation's "Add text?")
        const chId = state.selections[c.id];
        if (!chId) return null;
        const ch = c.choices.find((x) => x.id === chId);
        target[c.variantOption] = ch && ch.variantValue != null ? ch.variantValue : ch ? ch.label : null;
      }
      return VARIANTS.find((v) => OPTION_NAMES.every((n) => String(v.options[n]) === String(target[n]))) || null;
    }

    // ---- Hide the PDP variant pickers the customizer now drives -------------------
    // Add-on options (e.g. Text, Thickness) are real Shopify options so their price is
    // charged via the variant, but the shopper picks them INSIDE the customizer — not on
    // the product page. Hide those option groups in Horizon's storefront variant-picker,
    // leaving only the config key option (e.g. Size) visible. Names are collected across
    // ALL per-variant configs so a size that omits an add-on still hides its PDP picker.
    function persoHiddenOptionNames() {
      const names = new Set();
      const scan = (surfaces) => (surfaces || []).forEach((s) => (s.components || []).forEach((c) => {
        if (c.type === "option" && c.variantOption) names.add(c.variantOption);
      }));
      if (RAWCFG.map && typeof RAWCFG.map === "object") Object.keys(RAWCFG.map).forEach((k) => scan(RAWCFG.map[k].surfaces));
      else scan(RAWCFG.surfaces);
      return [...names];
    }
    function hidePdpVariantPickers() {
      const names = persoHiddenOptionNames();
      if (!names.length) return;
      // Primary: a `:has()` rule survives variant-picker morphs (re-render on option change).
      const STYLE_ID = "perso-hide-variant-pickers";
      let styleEl = document.getElementById(STYLE_ID);
      if (!styleEl) { styleEl = document.createElement("style"); styleEl.id = STYLE_ID; document.head.appendChild(styleEl); }
      styleEl.textContent = names
        .map((n) => `.variant-option:has([data-option-name="${n.replace(/["\\]/g, "\\$&")}"])`)
        .join(",") + "{display:none !important;}";
      // Fallback for engines without :has(): hide matching groups directly.
      document.querySelectorAll(".variant-option").forEach((grp) => {
        const el = grp.querySelector("[data-option-name]");
        const nm = el ? el.getAttribute("data-option-name") : null;
        if (nm && names.indexOf(nm) !== -1) grp.style.display = "none";
      });
    }
    hidePdpVariantPickers();
    const vpEl = document.querySelector("variant-picker");
    if (vpEl) new MutationObserver(() => hidePdpVariantPickers()).observe(vpEl, { childList: true, subtree: true });

    const state = {
      surfaceIdx: 0,
      selections: {}, values: {}, fonts: {}, colors: {},
      objects: {}, activeId: null, charsetErr: {}
    };

    const surface = () => CONFIG.surfaces[state.surfaceIdx];
    const allComponents = () => surface().components;        // CURRENT surface — canvas render + pointer
    const allComps = () => CONFIG.surfaces.flatMap((s) => s.components); // ALL surfaces — controls/validity/price/reveals
    const comp = (id) => allComps().find((c) => c.id === id);
    const surfaceIdxOf = (id) => { for (let i = 0; i < CONFIG.surfaces.length; i++) if (CONFIG.surfaces[i].components.some((c) => c.id === id)) return i; return state.surfaceIdx; };
    // the canvas follows whatever component the shopper is working on
    const showSurfaceOf = (id) => { const i = surfaceIdxOf(id); if (i !== state.surfaceIdx) { state.surfaceIdx = i; if (surfacesEl) buildSurfaces(); } };

    /* ---------- conditional reveals ---------- */
    function revealTargets() {
      const map = {};
      for (const c of allComps())
        if (c.type === "option")
          for (const ch of c.choices)
            for (const t of ch.reveals || []) (map[t] = map[t] || []).push({ optionId: c.id, choiceId: ch.id });
      return map;
    }
    function isVisible(c) {
      const rt = revealTargets()[c.id];
      if (!rt) return true;
      return rt.some((r) => state.selections[r.optionId] === r.choiceId);
    }

    /* ---------- charset validation ---------- */
    function charsetOk(kind, s) {
      if (!s) return true;
      const map = {
        "numbers-english": /^[0-9 ]*$/, "numbers-all": /^[0-9 ]*$/,
        "alphanumeric-english": /^[a-zA-Z0-9 ]*$/, "allcaps-english": /^[A-Z0-9 ]*$/,
        "all-excluding-emoji": /^[^\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}]*$/u,
        "all-including-emoji": /.*/s
      };
      return (map[kind] || /.*/).test(s);
    }

    /* ================= CANVAS ENGINE ================= */
    let RES = 800;
    // The surface actually shown on the canvas. Usually the current surface, but a
    // detail-only surface with no mockup (e.g. "Other Information", baseImageUrl null)
    // falls back to the nearest previous surface that HAS a mockup (see displaySurfaceIdx),
    // so the buyer keeps seeing the frame preview instead of a blank canvas.
    let dispIdx = 0;
    // Some configs (e.g. converted from Amazon templates) omit previewSize; the coordinate
    // space is 400×400, so default to that rather than crashing the whole render. Keyed off
    // the DISPLAYED surface so the drawn art aligns with the drawn mockup.
    const psOf = () => (CONFIG.surfaces[dispIdx] && CONFIG.surfaces[dispIdx].previewSize) || { width: 400, height: 400 };
    function syncCanvasSize() {
      dispIdx = displaySurfaceIdx();
      const ps = psOf();
      RES = 800;
      canvas.width = RES;
      canvas.height = Math.round(RES / (ps.width / ps.height));
    }
    const toPx = (v) => v * (RES / psOf().width);
    function getVar(n) {
      return getComputedStyle(root).getPropertyValue(n).trim() || "#888";
    }

    // ---- mockup / mask / choice-preview image cache ----
    const IMG = {};
    function imgGet(url) {
      if (!url) return null;
      if (IMG[url]) return IMG[url]._img || null;
      const im = new Image(); IMG[url] = { _img: null };
      im.onload = () => { IMG[url]._img = im; render(); };
      try { im.crossOrigin = "anonymous"; } catch (e) {}
      im.src = url; return null;
    }
    const artOff = document.createElement("canvas");
    // the surface's mockup, swapped to a selected option choice's previewUrl when one is set
    function surfaceImageUrl(idx = state.surfaceIdx) {
      const s = CONFIG.surfaces[idx]; let u = s.baseImageUrl || null;
      for (const c of s.components) if (c.type === "option" && isVisible(c)) {
        const ch = c.choices.find((x) => x.id === state.selections[c.id]);
        if (ch && ch.previewUrl) u = ch.previewUrl;
      }
      return u;
    }
    // a selected option choice can also carry its own clip mask (e.g. per-orientation frame)
    function surfaceMaskUrl(idx = state.surfaceIdx) {
      const s = CONFIG.surfaces[idx]; let m = s.maskImageUrl || null;
      for (const c of s.components) if (c.type === "option" && isVisible(c)) {
        const ch = c.choices.find((x) => x.id === state.selections[c.id]);
        if (ch && ch.maskUrl) m = ch.maskUrl;
      }
      return m;
    }
    // Which surface to actually paint: the current one if it resolves to a mockup image,
    // otherwise the nearest previous surface that does. Lets detail-only surfaces (e.g.
    // "Other Information", which has no baseImageUrl) keep showing the frame preview + the
    // buyer's placed art instead of a blank canvas.
    function displaySurfaceIdx() {
      if (surfaceImageUrl(state.surfaceIdx)) return state.surfaceIdx;
      for (let i = state.surfaceIdx - 1; i >= 0; i--) if (surfaceImageUrl(i)) return i;
      return state.surfaceIdx;
    }
    // auto-fit text into its print-area box (base px, before the object's own scale)
    function fitFontSize(c) {
      const pa = c.printArea, boxW = toPx(pa.width), boxH = toPx(pa.height);
      const fam = fontStack(state.fonts[c.id]);
      const txt = (state.values[c.id] || "").trim(); if (!txt) return toPx(pa.height) * 0.42;
      const lines = txt.split("\n").slice(0, c.maxLines || 1);
      const longest = lines.reduce((a, b) => (a.length >= b.length ? a : b), "");
      ctx.save(); ctx.font = `100px ${fam}`; const w100 = ctx.measureText(longest).width || 1; ctx.restore();
      return Math.max(6, Math.min((boxW * 0.92) / w100 * 100, (boxH * 0.82) / lines.length));
    }
    // bounds of an object's actual content (follows position/scale/rotation) — for the selection box + handles
    function objBounds(c) {
      const o = ensureObject(c), pa = c.printArea;
      if (c.type === "image") {
        if (!o.img) return { cx: toPx(o.x), cy: toPx(o.y), hw: toPx(pa.width) / 2, hh: toPx(pa.height) / 2, rot: o.angle };
        const iw = o.img.naturalWidth, ih = o.img.naturalHeight;
        const cover = Math.max(toPx(pa.width) / iw, toPx(pa.height) / ih) * o.scale;
        return { cx: toPx(o.x), cy: toPx(o.y), hw: (iw * cover) / 2, hh: (ih * cover) / 2, rot: o.angle };
      }
      const txt = (state.values[c.id] || "").trim();
      const fam = fontStack(state.fonts[c.id]);
      const size = fitFontSize(c) * o.scale;
      const nLines = Math.max(1, txt.split("\n").slice(0, c.maxLines || 1).length);
      ctx.save(); ctx.font = `${size}px ${fam}`;
      const tw = txt ? Math.max(...txt.split("\n").map((l) => ctx.measureText(l).width)) : toPx(pa.width) * 0.5;
      ctx.restore();
      return { cx: toPx(o.x), cy: toPx(o.y), hw: tw / 2 + 6, hh: (size * nLines * 0.62) + 4, rot: o.angle };
    }
    // contextual move/zoom/rotate toolbar under the canvas (rotate = desktop only), mirrors the Builder
    let handleDrag = null;
    function updateTools() {
      if (!toolsEl) return;
      const c = state.activeId ? comp(state.activeId) : null;
      const has = c && ((c.type === "image" && state.objects[c.id] && state.objects[c.id].img) || (c.type === "text" && (state.values[c.id] || "").trim()));
      if (!has) { toolsEl.innerHTML = ""; return; }
      const at = c.allowTransform || {};
      toolsEl.innerHTML = `<span class="perso-tool-lab">${c.label}</span>` +
        (at.zoom !== false ? `<button type="button" class="perso-tool" data-t="zout" aria-label="Zoom out">−</button><button type="button" class="perso-tool" data-t="zin" aria-label="Zoom in">+</button>` : "") +
        (at.rotate !== false && !isMobile() ? `<button type="button" class="perso-tool" data-t="rl" aria-label="Rotate left">⟲</button><button type="button" class="perso-tool" data-t="rr" aria-label="Rotate right">⟳</button>` : "") +
        `<button type="button" class="perso-tool" data-t="reset">Reset</button>`;
      const o = ensureObject(c);
      toolsEl.querySelectorAll("button").forEach((b) => b.onclick = () => {
        const t = b.dataset.t;
        if (t === "zout") o.scale = clamp(o.scale * 0.9, 0.2, 6);
        else if (t === "zin") o.scale = clamp(o.scale * 1.1, 0.2, 6);
        else if (t === "rl") o.angle -= 15;
        else if (t === "rr") o.angle += 15;
        else if (t === "reset") { const pa = c.printArea; o.x = pa.x + pa.width / 2; o.y = pa.y + pa.height / 2; o.scale = 1; o.angle = 0; }
        render();
      });
    }

    function ensureObject(c) {
      if (state.objects[c.id]) return state.objects[c.id];
      const pa = c.printArea;
      const o = { type: c.type, x: pa.x + pa.width / 2, y: pa.y + pa.height / 2, scale: 1, angle: 0, printArea: pa, img: null };
      state.objects[c.id] = o;
      return o;
    }

    function drawFrameBase() {
      ctx.fillStyle = getVar("--perso-surface");
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = getVar("--perso-line-strong");
      ctx.lineWidth = 6;
      ctx.strokeRect(6, 6, canvas.width - 12, canvas.height - 12);
    }

    // art (photo/text) drawn onto an offscreen layer so the mask can clip/overlay it
    function drawArt(octx, c) {
      const o = ensureObject(c), pa = c.printArea;
      octx.save();
      if (c.clip !== false) { octx.beginPath(); octx.rect(toPx(pa.x), toPx(pa.y), toPx(pa.width), toPx(pa.height)); octx.clip(); }
      octx.translate(toPx(o.x), toPx(o.y));
      octx.rotate((o.angle * Math.PI) / 180);
      octx.scale(o.scale, o.scale);
      if (c.type === "image" && o.img) {
        const paW = toPx(pa.width), paH = toPx(pa.height), iw = o.img.naturalWidth, ih = o.img.naturalHeight;
        const cover = Math.max(paW / iw, paH / ih);
        octx.drawImage(o.img, -(iw * cover) / 2, -(ih * cover) / 2, iw * cover, ih * cover);
      } else if (c.type === "text") {
        const txt = (state.values[c.id] || "").trim();
        if (txt) {
          const fam = fontStack(state.fonts[c.id]);
          const size = fitFontSize(c);
          octx.font = `${size}px ${fam}`; octx.fillStyle = state.colors[c.id] || "#111";
          octx.textAlign = "center"; octx.textBaseline = "middle";
          const lines = txt.split("\n").slice(0, c.maxLines || 1), lh = size * 1.06;
          lines.forEach((ln, i) => octx.fillText(ln, 0, (i - (lines.length - 1) / 2) * lh));
        }
      }
      octx.restore();
    }
    // selection outline + empty-state hint, drawn on top so they stay visible over the mask
    function drawDecor(c) {
      const o = ensureObject(c), active = state.activeId === c.id;
      const empty = (c.type === "image" && !o.img) || (c.type === "text" && !(state.values[c.id] || "").trim());
      // Only the active selection box + handles (matches the Builder — no empty-state hint text or dashed boxes).
      if (active && !empty) {
        const b = objBounds(c), at = c.allowTransform || {};
        ctx.save(); ctx.translate(b.cx, b.cy); ctx.rotate((b.rot * Math.PI) / 180);
        ctx.strokeStyle = getVar("--perso-accent"); ctx.lineWidth = 2; ctx.setLineDash([7, 5]);
        ctx.strokeRect(-b.hw, -b.hh, b.hw * 2, b.hh * 2); ctx.setLineDash([]);
        const hs = 12, cs = [[-b.hw, -b.hh], [b.hw, -b.hh], [b.hw, b.hh], [-b.hw, b.hh]];
        if (at.zoom !== false) { ctx.fillStyle = "#fff"; cs.forEach(([x, y]) => { ctx.fillRect(x - hs / 2, y - hs / 2, hs, hs); ctx.strokeRect(x - hs / 2, y - hs / 2, hs, hs); }); }
        if (at.rotate !== false && !isMobile()) { ctx.beginPath(); ctx.moveTo(0, -b.hh); ctx.lineTo(0, -b.hh - 30); ctx.stroke(); ctx.beginPath(); ctx.arc(0, -b.hh - 30, 8, 0, Math.PI * 2); ctx.fillStyle = "#fff"; ctx.fill(); ctx.stroke(); }
        ctx.restore();
      }
    }

    function render() {
      syncCanvasSize();   // resolves dispIdx (current surface, or a mockup-bearing fallback)
      const W = canvas.width, H = canvas.height;
      const ds = CONFIG.surfaces[dispIdx];
      const base = imgGet(surfaceImageUrl(dispIdx));
      if (base) ctx.drawImage(base, 0, 0, W, H); else drawFrameBase();
      artOff.width = W; artOff.height = H;
      const octx = artOff.getContext("2d"); octx.clearRect(0, 0, W, H);
      const placeables = ds.components.filter((c) => (c.type === "image" || (c.type === "text" && c.printArea)) && isVisible(c));
      let painted = false;
      for (const c of placeables) { drawArt(octx, c); painted = true; }
      const murl = surfaceMaskUrl(dispIdx), mask = murl ? imgGet(murl) : null;
      if (mask && ds.maskMode === "clip") { octx.save(); octx.globalCompositeOperation = "destination-in"; octx.drawImage(mask, 0, 0, W, H); octx.restore(); }
      if (painted) ctx.drawImage(artOff, 0, 0);
      if (mask && ds.maskMode !== "clip") ctx.drawImage(mask, 0, 0, W, H);
      for (const c of placeables) drawDecor(c);
    }

    /* ---------- pointer interaction ---------- */
    const pointers = new Map();
    let dragStart = null, pinchStart = null;
    function canvasPoint(e) {
      const r = canvas.getBoundingClientRect();
      return { x: (e.clientX - r.left) * (canvas.width / r.width), y: (e.clientY - r.top) * (canvas.height / r.height) };
    }
    function placeablesTopFirst() {
      return allComponents().filter((c) => (c.type === "image" || (c.type === "text" && c.printArea)) && isVisible(c)).reverse();
    }
    function hitTest(p) {
      for (const c of placeablesTopFirst()) {
        const o = state.objects[c.id];
        const hasContent = (c.type === "image" && o && o.img) || (c.type === "text" && (state.values[c.id] || "").trim());
        if (hasContent) {
          const b = objBounds(c), dx = p.x - b.cx, dy = p.y - b.cy, a = -b.rot * Math.PI / 180;
          const lx = dx * Math.cos(a) - dy * Math.sin(a), ly = dx * Math.sin(a) + dy * Math.cos(a), pad = c.type === "text" ? 8 : 0;
          if (Math.abs(lx) <= b.hw + pad && Math.abs(ly) <= b.hh + pad) return c.id;
        } else {
          const pa = c.printArea;
          if (p.x >= toPx(pa.x) && p.x <= toPx(pa.x + pa.width) && p.y >= toPx(pa.y) && p.y <= toPx(pa.y + pa.height)) return c.id;
        }
      }
      return null;
    }
    canvas.addEventListener("pointerdown", (e) => {
      canvas.setPointerCapture(e.pointerId);
      const p = canvasPoint(e);
      pointers.set(e.pointerId, p);
      if (pointers.size === 1) {
        // 1) grab a handle on the active element (corner = resize, top = rotate)
        if (state.activeId) {
          const c = comp(state.activeId), b = objBounds(c), at = c.allowTransform || {};
          const dx = p.x - b.cx, dy = p.y - b.cy, a = -b.rot * Math.PI / 180;
          const lx = dx * Math.cos(a) - dy * Math.sin(a), ly = dx * Math.sin(a) + dy * Math.cos(a), tol = 18;
          if (at.rotate !== false && !isMobile() && Math.hypot(lx, ly + b.hh + 30) < tol) {
            handleDrag = { mode: "rotate", cx: b.cx, cy: b.cy, r0: ensureObject(c).angle, a0: Math.atan2(dy, dx) * 180 / Math.PI }; dragStart = null; return;
          }
          if (at.zoom !== false) for (const [hx, hy] of [[-b.hw, -b.hh], [b.hw, -b.hh], [b.hw, b.hh], [-b.hw, b.hh]]) if (Math.hypot(lx - hx, ly - hy) < tol) {
            handleDrag = { mode: "scale", cx: b.cx, cy: b.cy, s0: ensureObject(c).scale, d0: Math.hypot(dx, dy) || 1 }; dragStart = null; return;
          }
        }
        // 2) otherwise select / move the topmost content under the pointer
        const id = hitTest(p);
        if (id) { state.activeId = id; const o = state.objects[id]; dragStart = { px: p.x, py: p.y, ox: o.x, oy: o.y }; }
        else { state.activeId = null; dragStart = null; }
        buildControls(); render();
      } else if (pointers.size === 2) {
        const pts = [...pointers.values()], o = state.objects[state.activeId];
        if (o) { pinchStart = { d: dist(pts[0], pts[1]), a: ang(pts[0], pts[1]), scale: o.scale, angle: o.angle }; dragStart = null; handleDrag = null; }
      }
    });
    canvas.addEventListener("pointermove", (e) => {
      if (!pointers.has(e.pointerId)) return;
      const p = canvasPoint(e); pointers.set(e.pointerId, p);
      const o = state.objects[state.activeId];
      if (handleDrag && o) {
        const at = (comp(state.activeId).allowTransform) || {};
        if (handleDrag.mode === "scale" && at.zoom !== false) o.scale = clamp(handleDrag.s0 * (Math.hypot(p.x - handleDrag.cx, p.y - handleDrag.cy) / handleDrag.d0), 0.2, 6);
        else if (handleDrag.mode === "rotate" && at.rotate !== false) o.angle = handleDrag.r0 + (Math.atan2(p.y - handleDrag.cy, p.x - handleDrag.cx) * 180 / Math.PI - handleDrag.a0);
        render(); return;
      }
      if (pointers.size === 2 && pinchStart && o) {
        const pts = [...pointers.values()], at = (comp(state.activeId).allowTransform) || {};
        if (at.zoom !== false) o.scale = clamp(pinchStart.scale * (dist(pts[0], pts[1]) / pinchStart.d), 0.2, 6);
        if (at.rotate !== false) o.angle = pinchStart.angle + ((ang(pts[0], pts[1]) - pinchStart.a) * 180) / Math.PI;
        render();
      } else if (pointers.size === 1 && dragStart && o) {
        if ((comp(state.activeId).allowTransform || {}).drag === false) return;
        const inv = psOf().width / RES;
        o.x = dragStart.ox + (p.x - dragStart.px) * inv;
        o.y = dragStart.oy + (p.y - dragStart.py) * inv;
        render();
      }
    });
    function endPtr(e) { pointers.delete(e.pointerId); if (pointers.size < 2) pinchStart = null; if (pointers.size === 0) { dragStart = null; handleDrag = null; } updateTools(); }
    canvas.addEventListener("pointerup", endPtr);
    canvas.addEventListener("pointercancel", endPtr);
    canvas.addEventListener("wheel", (e) => {
      const o = state.objects[state.activeId]; if (!o) return; e.preventDefault();
      if ((comp(state.activeId).allowTransform || {}).zoom === false) return;
      o.scale = clamp(o.scale * (e.deltaY < 0 ? 1.06 : 0.94), 0.2, 5); syncXformUI(); render();
    }, { passive: false });

    /* ================= CONTROLS UI ================= */
    function buildControls() {
      controlsEl.innerHTML = "";
      // ALL surfaces shown in one scrolling column (no click-to-reveal tabs) — matches the Builder.
      const multi = CONFIG.surfaces.length > 1;
      CONFIG.surfaces.forEach((s) => {
        if (multi) { const h = document.createElement("div"); h.className = "perso-surf-title"; h.textContent = s.label; controlsEl.appendChild(h); }
        for (const c of s.components) {
          const g = groupEl(c.label, c.required, c.instructions, componentSelectionLabel(c));
          if (!isVisible(c)) g.group.classList.add("perso-hidden");
          if (c.type === "option") renderOption(c, g);
          else if (c.type === "image") renderImage(c, g);
          else if (c.type === "text" || c.type === "data") renderText(c, g);
          controlsEl.appendChild(g.group);
        }
      });
      updateTools();
    }
    function componentSelectionLabel(c) {
      if (c.type === "option" && state.selections[c.id]) {
        const ch = c.choices.find((x) => x.id === state.selections[c.id]);
        return ch ? ch.label : "";
      }
      return "";
    }
    function groupEl(label, required, instr, sel) {
      const group = document.createElement("section");
      group.className = "perso-group";
      const head = document.createElement("div");
      head.className = "perso-g-head";
      head.innerHTML = `<div class="perso-lab"><span>${label}</span>${required ? '<span class="perso-req">*</span>' : '<span class="perso-optional">(Optional)</span>'}
        <span class="perso-sel">${sel || ""}</span></div>${instr ? `<div class="perso-instr">${instr}</div>` : ""}`;
      const body = document.createElement("div");
      body.className = "perso-g-body";
      group.appendChild(head); group.appendChild(body);
      return { group, body };
    }

    function renderOption(c, g) {
      if (c.display === "image-cards") {
        const wrap = document.createElement("div"); wrap.className = "perso-cards";
        for (const ch of c.choices) {
          const b = document.createElement("button"); b.type = "button"; b.className = "perso-card";
          b.setAttribute("aria-pressed", state.selections[c.id] === ch.id);
          b.innerHTML = `<div class="perso-thumb">${ch.thumbUrl ? `<img src="${ch.thumbUrl}" alt="${ch.label}">` : ch.label}</div><div class="perso-cap">${ch.label}${ch.priceDelta ? ` · +${money(ch.priceDelta, CUR)}` : ""}</div>`;
          b.onclick = () => selectChoice(c, ch); wrap.appendChild(b);
        }
        g.body.appendChild(wrap);
      } else {
        const seg = document.createElement("div"); seg.className = "perso-seg";
        for (const ch of c.choices) {
          const b = document.createElement("button"); b.type = "button"; b.className = "perso-chip";
          b.setAttribute("aria-pressed", state.selections[c.id] === ch.id);
          b.innerHTML = `${ch.label}${ch.priceDelta ? `<span class="perso-delta">+${money(ch.priceDelta, CUR)}</span>` : ""}`;
          b.onclick = () => selectChoice(c, ch); seg.appendChild(b);
        }
        g.body.appendChild(seg);
      }
    }
    function selectChoice(c, ch) { state.selections[c.id] = ch.id; showSurfaceOf(c.id); buildControls(); updatePrice(); render(); }

    function renderImage(c, g) {
      const o = ensureObject(c);
      const drop = document.createElement("label"); drop.className = "perso-drop" + (o.img ? " has" : "");
      const input = document.createElement("input"); input.type = "file"; input.accept = "image/*"; input.hidden = true;
      drop.appendChild(input);
      const view = document.createElement("div");
      function paint() {
        if (o.img) {
          view.className = "perso-thumbrow"; view.innerHTML = "";
          const im = document.createElement("img"); im.src = o.img.src; view.appendChild(im);
          const meta = document.createElement("div");
          meta.innerHTML = `<div class="perso-fn">${o._name || "image"}</div>`;
          view.appendChild(meta);
          drop.textContent = "Replace image"; drop.className = "perso-drop has"; drop.appendChild(input);
        } else {
          drop.innerHTML = "<b>Tap to upload</b> or drop an image here"; drop.appendChild(input);
          view.className = ""; view.innerHTML = "";
        }
      }
      input.onchange = (e) => { const f = e.target.files[0]; if (f) loadImageFile(c, o, f, () => { paint(); buildControls(); render(); }); };
      drop.addEventListener("dragover", (e) => e.preventDefault());
      drop.addEventListener("drop", (e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) loadImageFile(c, o, f, () => { paint(); buildControls(); render(); }); });
      g.body.appendChild(drop); g.body.appendChild(view); paint();
    }
    function loadImageFile(c, o, f, cb) {
      const rd = new FileReader();
      rd.onload = () => { const im = new Image(); im.onload = () => {
        o.img = im; o._name = f.name; o._file = f;
        // reset to the default centered position so a replacement never inherits the old placement
        const pa = c.printArea; o.x = pa.x + pa.width / 2; o.y = pa.y + pa.height / 2; o.scale = 1; o.angle = 0;
        state.activeId = c.id; showSurfaceOf(c.id); cb();
      }; im.src = rd.result; };
      rd.readAsDataURL(f);
    }

    function renderText(c, g) {
      const isPlaced = !!c.printArea;
      const wrap = document.createElement("div");
      const inputEl = c.maxLines && c.maxLines > 1 ? document.createElement("textarea") : document.createElement("input");
      if (inputEl.tagName === "INPUT") inputEl.type = "text";
      inputEl.placeholder = c.placeholder || ""; inputEl.value = state.values[c.id] || "";
      if (c.maxLength) inputEl.maxLength = c.maxLength + 20;
      const meta = document.createElement("div"); meta.className = "perso-field-meta";
      function upd() {
        let v = inputEl.value;
        if (c.maxLength && v.length > c.maxLength) { v = v.slice(0, c.maxLength); inputEl.value = v; }
        state.values[c.id] = v;
        const bad = !charsetOk(c.charset, v) || (c.required && !v.trim()) || (v.trim() && c.minLength && v.trim().length < c.minLength);
        state.charsetErr[c.id] = bad;
        meta.innerHTML = `<span class="${bad ? "perso-err" : ""}">${errMsg(c, v)}</span>` + (c.maxLength ? `<span>${v.length}/${c.maxLength}</span>` : "");
        if (isPlaced && isVisible(c) && document.activeElement === inputEl) state.activeId = c.id;
        render(); updateCTA(); updateTools();
      }
      inputEl.addEventListener("input", upd);
      if (isPlaced) inputEl.addEventListener("focus", () => { state.activeId = c.id; showSurfaceOf(c.id); render(); });
      wrap.appendChild(inputEl); wrap.appendChild(meta);

      if (c.font) {
        if (!state.fonts[c.id]) state.fonts[c.id] = c.font.default || c.font.options[0].family;
        const lab = document.createElement("div"); lab.className = "perso-field-meta"; lab.innerHTML = `<span>${c.font.label || "Font"}</span>`;
        // Custom font list (each chip rendered IN its own font) — native <select> ignores per-option
        // font-family on mobile, so buyers couldn't see the styles.
        const list = document.createElement("div"); list.className = "perso-fontlist";
        for (const f of c.font.options) {
          const b = document.createElement("button"); b.type = "button"; b.className = "perso-fontchip";
          b.textContent = f.family; b.style.fontFamily = fontStack(f.family);
          b.setAttribute("aria-pressed", state.fonts[c.id] === f.family);
          b.onclick = () => { state.fonts[c.id] = f.family; list.querySelectorAll(".perso-fontchip").forEach((x) => x.setAttribute("aria-pressed", "false")); b.setAttribute("aria-pressed", "true"); render(); };
          list.appendChild(b);
        }
        g.body.appendChild(lab); g.body.appendChild(list);
      }
      if (c.color) {
        if (!state.colors[c.id]) state.colors[c.id] = (c.color.options.find((o) => o.name === c.color.default) || c.color.options[0]).hex;
        const lab = document.createElement("div"); lab.className = "perso-field-meta"; lab.innerHTML = `<span>${c.color.label || "Color"}</span>`;
        const sw = document.createElement("div"); sw.className = "perso-swatches";
        for (const o of c.color.options) {
          const b = document.createElement("button"); b.type = "button"; b.className = "perso-swatch"; b.style.background = o.hex; b.title = o.name;
          b.setAttribute("aria-pressed", state.colors[c.id] === o.hex);
          b.onclick = () => { state.colors[c.id] = o.hex; buildControls(); render(); };
          sw.appendChild(b);
        }
        g.body.appendChild(lab); g.body.appendChild(sw);
      }
      g.body.appendChild(wrap);   // text input goes LAST → Font Style & Colour appear above it
      upd();
    }
    function errMsg(c, v) {
      if (c.required && !v.trim()) return "Required";
      if (!charsetOk(c.charset, v)) return "Invalid characters";
      if (v.trim() && c.minLength && v.trim().length < c.minLength) return `Min ${c.minLength} chars`;
      return "";
    }

    function xformUI(c) {
      const o = ensureObject(c), at = c.allowTransform || {};
      const box = document.createElement("div"); box.className = "perso-xform"; box.dataset.for = c.id;
      const mk = (key, label, min, max, step, val, fmt, on) => {
        const row = document.createElement("div"); row.className = "perso-xrow";
        row.innerHTML = `<label>${label}</label>`;
        const r = document.createElement("input"); r.type = "range"; r.min = min; r.max = max; r.step = step; r.value = val;
        const out = document.createElement("output"); out.textContent = fmt(val);
        r.oninput = () => { on(parseFloat(r.value)); out.textContent = fmt(parseFloat(r.value)); render(); };
        r.dataset.key = key;
        row.appendChild(r); row.appendChild(out); box.appendChild(row);
      };
      if (at.zoom !== false) mk("scale", "Zoom", 0.2, 5, 0.01, o.scale, (v) => `${Math.round(v * 100)}%`, (v) => (o.scale = v));
      if (at.rotate !== false) mk("angle", "Rotate", -180, 180, 1, o.angle, (v) => `${Math.round(v)}°`, (v) => (o.angle = v));
      return box;
    }
    function syncXformUI() {
      const o = state.objects[state.activeId]; if (!o) return;
      root.querySelectorAll(`.perso-xform[data-for="${state.activeId}"] input[type=range]`).forEach((r) => {
        if (r.dataset.key === "scale") { r.value = o.scale; r.nextSibling.textContent = `${Math.round(o.scale * 100)}%`; }
        if (r.dataset.key === "angle") { r.value = o.angle; r.nextSibling.textContent = `${Math.round(o.angle)}°`; }
      });
    }

    /* ---------- surface thumbnail strip (switches only the CANVAS view; all controls are always shown) ---------- */
    function buildSurfaces() {
      const el = surfacesEl; if (!el) return; el.innerHTML = "";
      if (surfLabelEl) surfLabelEl.textContent = surface().label;
      if (CONFIG.surfaces.length < 2) { el.style.display = "none"; return; }
      el.style.display = "";
      CONFIG.surfaces.forEach((s, i) => {
        const b = document.createElement("button"); b.type = "button"; b.className = "perso-surf-thumb"; b.title = s.label;
        b.setAttribute("aria-selected", i === state.surfaceIdx);
        b.innerHTML = s.baseImageUrl ? `<img src="${s.baseImageUrl}" alt="${s.label}">` : `<span>${(s.label || "").slice(0, 8)}</span>`;
        b.onclick = () => { state.surfaceIdx = i; state.activeId = null; buildSurfaces(); syncCanvasSize(); render(); updateTools(); };
        el.appendChild(b);
      });
    }

    /* ================= PRICING ================= */
    function deltaTotal() {
      let d = 0;
      for (const c of allComps()) {
        if (!isVisible(c)) continue;
        if (c.type === "option") { const ch = c.choices.find((x) => x.id === state.selections[c.id]); if (ch) d += ch.priceDelta || 0; }
        else if (typeof c.priceDelta === "number") d += c.priceDelta;
      }
      return d;
    }
    function updatePrice() {
      if (isVariantPriced()) {
        const v = resolveVariant();
        if (v && formIdInput) formIdInput.value = v.id;
        // Always show a real total: the exact variant price once fully resolved, otherwise the
        // size's base price plus whatever add-ons are picked so far (grows as they choose). The
        // two agree — a resolved variant's price equals base + its add-on deltas — so the number
        // never jumps oddly, and the shopper never sees a bare "—".
        const sb = sizeBaseVariant();
        const shown = v ? v.price : ((sb ? sb.price : (CONFIG.basePrice || 0)) + deltaTotal());
        priceEl.textContent = money(shown, CUR);
        priceBreakEl.textContent = (v && !v.available) ? "Selected combination unavailable" : "";
      } else {
        // No variant-backed options: start from the REAL currently-selected variant
        // price (Shape/Size chosen on the product page) if we have variant data, else
        // the config's basePrice; then add any illustrative option deltas.
        const bv = baseVariant();
        const base = bv ? bv.price : (CONFIG.basePrice || 0);
        const d = deltaTotal();
        priceEl.textContent = money(base + d, CUR);
        priceBreakEl.textContent = d > 0 ? `+ options ${money(d, CUR)}` : "";
      }
      updateCTA();
    }
    function validityIssues() {
      const issues = [];
      for (const c of allComps()) {
        if (!isVisible(c)) continue;
        if (c.type === "option" && c.required && !state.selections[c.id]) issues.push(c.label);
        if (c.type === "image" && c.required && !(state.objects[c.id] && state.objects[c.id].img)) issues.push(c.label);
        if (c.type === "text" || c.type === "data") { const v = state.values[c.id] || ""; if ((c.required && !v.trim()) || state.charsetErr[c.id]) issues.push(c.label); }
      }
      if (isVariantPriced()) {
        const v = resolveVariant();
        if (!v) issues.push("Options");
        else if (!v.available) issues.push("Availability");
      }
      return issues;
    }
    function updateCTA() {
      const ok = validityIssues().length === 0;
      const btn = saveBtn;
      btn.disabled = !ok;
      btn.textContent = ok ? "Add to cart" : "Complete required fields";
    }

    /* ================= RESULT / FORM WIRING ================= */
    function placement(o) {
      return { position: { x: round(o.x), y: round(o.y) }, scale: { scaleX: round(o.scale, 4), scaleY: round(o.scale, 4) }, angleOfRotation: round(o.angle, 3) };
    }
    function buildResult() {
      const items = [];
      for (const c of allComps()) {
        if (!isVisible(c)) continue;
        if (c.type === "image" && state.objects[c.id] && state.objects[c.id].img) {
          const o = state.objects[c.id];
          items.push({ componentId: c.id, label: c.label, type: "image", fileName: o._name, placement: placement(o) });
        } else if (c.type === "text" || c.type === "data") {
          const v = state.values[c.id] || ""; if (!v.trim() && !c.required) continue;
          const item = { componentId: c.id, label: c.label, type: "text", inputValue: v };
          if (c.printArea && state.objects[c.id]) { item.placement = placement(state.objects[c.id]); item.font = state.fonts[c.id]; item.color = state.colors[c.id]; }
          items.push(item);
        } else if (c.type === "option" && state.selections[c.id]) {
          const ch = c.choices.find((x) => x.id === state.selections[c.id]);
          items.push({ componentId: c.id, label: c.label, type: "option", value: state.selections[c.id], valueLabel: ch ? ch.label : state.selections[c.id] });
        }
      }
      const v = isVariantPriced() ? resolveVariant() : null;
      const price = v ? v.price : (CONFIG.basePrice || 0) + deltaTotal();
      return { productId: CONFIG.productId, variantId: v ? v.id : undefined, surface: surface().id, price, currency: CUR, items };
    }

    // Human-readable line-item properties + the machine-readable design blob + photo file.
    function writeFormProperties(result) {
      const propsBox = root.querySelector(".perso-props");
      // clear previously-generated readable inputs (keep the 3 fixed ones)
      propsBox.querySelectorAll("[data-perso-generated]").forEach((n) => n.remove());

      const set = (sel, val) => { const el = propsBox.querySelector(sel); if (el) { el.value = val; el.disabled = false; } };
      set('[data-perso-prop="summary"]', "Custom design");
      set('[data-perso-prop="design"]', JSON.stringify(result));

      // readable per-field props (visible in cart / on order)
      for (const it of result.items) {
        if (it.type === "image") continue; // the file itself is the Photo property
        const label = it.label.replace(/\[|\]/g, "");
        const val = it.type === "option" ? it.valueLabel : it.inputValue;
        if (!val) continue;
        const inp = document.createElement("input");
        inp.type = "hidden"; inp.name = `properties[${label}]`; inp.value = val;
        inp.dataset.persoGenerated = "1";
        propsBox.appendChild(inp);
      }

      // Native Shopify file line-item properties for the uploaded photos. EVERY image
      // component that has an upload becomes its own file on the order: the first reuses the
      // static `Photo` input (backward compatible with single-photo products, the cart edit
      // link and older orders), extras get `Photo 2`, `Photo 3`, ... created in the same order
      // as buildResult()'s image items — so the order carries ALL the buyer's source photos.
      const photoInput = propsBox.querySelector('[data-perso-prop="photo"]');
      if (photoInput) { try { photoInput.value = ""; } catch (e) {} photoInput.disabled = true; }
      let imgN = 0;
      for (const c of allComps()) {
        if (c.type !== "image") continue;
        if (!isVisible(c)) continue;   // skip slots hidden by the current selection (e.g. Pack of 4 hides slots 5-10)
        const st = state.objects[c.id];
        if (!st || !st._file) continue;
        imgN++;
        let inp;
        if (imgN === 1 && photoInput) {
          inp = photoInput;
        } else {
          inp = document.createElement("input");
          inp.type = "file";
          inp.name = `properties[Photo ${imgN}]`;
          inp.hidden = true;
          inp.dataset.persoGenerated = "1"; // cleared & rebuilt on every re-save (see top of fn)
          propsBox.appendChild(inp);
        }
        const dt = new DataTransfer();
        dt.items.add(st._file);
        inp.files = dt.files;
        inp.disabled = false;
      }
    }

    // ---- Edit design from cart --------------------------------------------------
    // Re-populates the customizer from a previously-saved `_Design` blob (written by
    // buildResult()/writeFormProperties() at save time). The design blob stores option/
    // text values + placements, but NOT the photo's pixels (only its fileName) — the
    // actual photo is recovered from the cart line item's uploaded `Photo` property URL
    // (Shopify's file-upload line-item property becomes a public URL once in the cart).
    // We re-fetch it as a Blob → File so a re-save can re-upload it (state.objects[id]._file
    // is what writeFormProperties() reads to populate the file input).
    function loadDesign(design, photos) {
      if (!design || !Array.isArray(design.items)) return;
      const sIdx = CONFIG.surfaces.findIndex((s) => s.id === design.surface);
      state.surfaceIdx = sIdx >= 0 ? sIdx : 0;
      for (const it of design.items) {
        const c = comp(it.componentId);
        if (!c || it.type === "image") continue;
        if (it.type === "option") state.selections[c.id] = it.value;
        else if (it.type === "text") {
          state.values[c.id] = it.inputValue || "";
          if (it.font) state.fonts[c.id] = it.font;
          if (it.color) state.colors[c.id] = it.color;
          if (it.placement && c.printArea) {
            const o = ensureObject(c);
            o.x = it.placement.position.x; o.y = it.placement.position.y;
            o.scale = it.placement.scale.scaleX; o.angle = it.placement.angleOfRotation;
          }
        }
      }
      const finish = () => { buildControls(); render(); updatePrice(); openModal(); };

      // Restore every uploaded photo. New edit links hand off an ARRAY of URLs in image order
      // (Photo, Photo 2, ...); legacy links hand off a single URL string. Each image slot is
      // re-hydrated from its matching URL so re-saving keeps all photos (no loss on cart edit).
      let photoUrls = [];
      if (Array.isArray(photos)) photoUrls = photos;
      else if (typeof photos === "string" && photos) photoUrls = [photos];

      const imgItems = design.items.filter((it) => it.type === "image");
      if (!imgItems.length) { finish(); return; }

      let pending = 0;
      const settle = () => { pending -= 1; if (pending <= 0) finish(); };
      imgItems.forEach((imgItem, i) => {
        const imgComp = comp(imgItem.componentId);
        const url = photoUrls[i];
        if (!imgComp || !url) return;
        pending += 1;
        const o = ensureObject(imgComp);
        fetch(url).then((r) => { if (!r.ok) throw new Error("fetch failed"); return r.blob(); }).then((blob) => {
          const file = new File([blob], imgItem.fileName || ("photo" + (i + 1) + ".jpg"), { type: blob.type || "image/jpeg" });
          const objUrl = URL.createObjectURL(blob);
          const im = new Image();
          im.onload = () => {
            o.img = im; o._name = file.name; o._file = file;
            if (imgItem.placement) { o.x = imgItem.placement.position.x; o.y = imgItem.placement.position.y; o.scale = imgItem.placement.scale.scaleX; o.angle = imgItem.placement.angleOfRotation; }
            settle();
          };
          im.onerror = settle;
          im.src = objUrl;
        }).catch((e) => { console.warn("[personalization] couldn't re-load a saved photo — buyer may need to re-upload", e); settle(); });
      });
      if (pending === 0) finish();
    }

    function finalize() {
      if (validityIssues().length) return;
      state.activeId = null; render();
      const result = buildResult();
      if (result.variantId && formIdInput) formIdInput.value = result.variantId;
      writeFormProperties(result);

      // NOTE: the old base64 `_Preview` data-URL property is intentionally no longer written —
      // it cluttered the admin order view. The cart thumbnail + production preview both use the
      // real file `_PreviewImage` written below (cart-products.liquid reads it, falling back to
      // `_Preview` only for any pre-upgrade cart items).

      // Full buyer-preview as a REAL image file on the order (production reference — exactly what
      // the buyer approved). Synchronous data-URL → File so it slots into the existing submit flow
      // with no timing change; if the canvas is tainted or anything throws we skip it and the order
      // still goes through. Downscaled to keep the upload small.
      try {
        const pf = root.querySelector('.perso-props [data-perso-prop="previewfile"]');
        if (pf) {
          const maxW = 1200;
          const k = Math.min(1, maxW / canvas.width);
          const pc = document.createElement("canvas");
          pc.width = Math.max(1, Math.round(canvas.width * k));
          pc.height = Math.max(1, Math.round(canvas.height * k));
          pc.getContext("2d").drawImage(canvas, 0, 0, pc.width, pc.height);
          const durl = pc.toDataURL("image/jpeg", 0.9);
          const bstr = atob(durl.slice(durl.indexOf(",") + 1));
          let n = bstr.length; const u8 = new Uint8Array(n);
          while (n--) u8[n] = bstr.charCodeAt(n);
          const pfile = new File([u8], "preview.jpg", { type: "image/jpeg" });
          const dt = new DataTransfer(); dt.items.add(pfile);
          pf.files = dt.files; pf.disabled = false;
        }
      } catch (e) { /* preview file is best-effort; never block add-to-cart */ }

      // preview thumbnail for the "done" panel (guard against a cross-origin-tainted canvas)
      let previewUrl = "";
      try { previewUrl = canvas.toDataURL("image/png"); } catch (e) {}
      const donePanel = root.querySelector("[data-perso-done-panel]");
      let thumb = donePanel.querySelector(".perso-done__thumb");
      if (!thumb) { thumb = document.createElement("img"); thumb.className = "perso-done__thumb"; thumb.alt = "Your design"; donePanel.querySelector(".perso-done__row").prepend(thumb); }
      if (previewUrl) thumb.src = previewUrl;

      // reveal the native buttons (so the product form submits normally) + mark done
      gate.classList.add("perso-done");
      root.querySelector("[data-perso-trigger]").hidden = true;
      donePanel.hidden = false;
      closeModal();

      // Add to cart directly: click the product form's native add-to-cart button, which
      // submits the multipart FormData (design props + uploaded Photo file) and updates the cart.
      const addBtn = (persoForm && persoForm.querySelector('[name="add"]')) ||
        (gate.querySelector && gate.querySelector('[name="add"]')) ||
        document.querySelector('product-form-component [name="add"]');
      if (addBtn) setTimeout(() => addBtn.click(), 30);
    }

    /* ---------- modal open/close ---------- */
    function openModal() {
      ensureFonts();    // lazy-load webfonts on first open (kept off the page-load path)
      ensureInited();   // first heavy render + image loads also deferred to first open
      modal.hidden = false; modal.setAttribute("aria-hidden", "false");
      document.documentElement.classList.add("perso-modal-open");
      render(); updatePrice();
    }
    function closeModal() {
      modal.hidden = true; modal.setAttribute("aria-hidden", "true");
      document.documentElement.classList.remove("perso-modal-open");
    }

    root.querySelectorAll("[data-perso-open]").forEach((b) => b.addEventListener("click", openModal));
    closeBtns.forEach((b) => b.addEventListener("click", closeModal));
    saveBtn.addEventListener("click", finalize);
    resetBtn.addEventListener("click", () => { const o = state.objects[state.activeId]; if (!o) return; const pa = o.printArea; o.x = pa.x + pa.width / 2; o.y = pa.y + pa.height / 2; o.scale = 1; o.angle = 0; buildControls(); render(); });
    centerBtn.addEventListener("click", () => { const o = state.objects[state.activeId]; if (!o) return; const pa = o.printArea; o.x = pa.x + pa.width / 2; o.y = pa.y + pa.height / 2; render(); });
    document.addEventListener("keydown", (e) => { if (e.key === "Escape" && !modal.hidden) closeModal(); });

    /* ================= INIT ================= */
    // Opt-in per-option default: if a config option declares "default": "<choiceId>", pre-select it
    // (only when nothing is chosen yet). No effect on options without a default, so other products
    // are untouched. Used e.g. by the mini-frame pack option to open on "Pack of 4".
    function applyOptionDefaults() {
      for (const c of allComps()) {
        if (c.type === "option" && c.default && state.selections[c.id] === undefined &&
            (c.choices || []).some((ch) => ch.id === c.default)) {
          state.selections[c.id] = c.default;
        }
      }
    }
    function initSurface() {
      if (surfLabelEl) surfLabelEl.textContent = surface().label;
      applyOptionDefaults();
      buildSurfaces(); buildControls(); syncCanvasSize(); render(); updatePrice();
    }
    // Defer the customizer's first heavy work — the canvas draw plus the surface/mask and
    // option-thumbnail image loads — until it's actually opened, so none of it sits on the
    // product page's load path. Both init and fonts funnel through openModal(); the
    // edit-from-cart flow reaches them via loadDesign()'s finish() → openModal().
    let _persoInited = false;
    const ensureInited = () => { if (_persoInited) return; _persoInited = true; initSurface(); };
    let _persoFontsRequested = false;
    const ensureFonts = () => { if (_persoFontsRequested) return; _persoFontsRequested = true; persoLoadFonts(RAWCFG, render); };
    window.addEventListener("resize", () => { if (_persoInited) render(); });

    // Pick up a design handed off from the cart's "Edit design" link (see cart-products.liquid).
    // The link stores {design, photoUrl} in sessionStorage and deep-links here with ?perso_edit=1
    // on the exact variant's product URL, so CONFIG is already resolved correctly above.
    try {
      if (new URLSearchParams(location.search).get("perso_edit") === "1") {
        const raw = sessionStorage.getItem("perso_edit_pending");
        if (raw) {
          sessionStorage.removeItem("perso_edit_pending");
          const pending = JSON.parse(raw);
          loadDesign(pending.design, (pending.photoUrls && pending.photoUrls.length) ? pending.photoUrls : pending.photoUrl);
        }
      }
    } catch (e) { console.warn("[personalization] edit-design handoff failed", e); }

    /* ---------- re-resolve config when the shopper changes the variant ---------- */
    // A Shape change swaps the whole customizer; a Size-only change keeps it (just the
    // price updates). We key off the RESOLVED config key, so we only reset the design
    // when the flow genuinely changes.
    function onVariantMaybeChanged() {
      const id = currentVariantId();
      if (!isMapped()) { if (_persoInited) updatePrice(); return; }        // legacy flat config: nothing to re-key
      const newKey = configKey(id);
      if (newKey === activeKey) { if (_persoInited) updatePrice(); return; } // same flow (e.g. only Size changed)
      activeKey = newKey;
      CONFIG = resolveConfig(id);
      // different customizer → start clean
      state.surfaceIdx = 0; state.selections = {}; state.values = {}; state.fonts = {};
      state.colors = {}; state.objects = {}; state.activeId = null; state.charsetErr = {};
      // Only re-render if the customizer is already open; otherwise the new flow is picked
      // up lazily on the next openModal() (keeps image loads off the page-load path).
      if (_persoInited) initSurface();
    }
    // Horizon updates input[name="id"] (ProductForm) and fires a bubbling product-select
    // event on variant change. Hook likely event names, and poll the id input as a
    // theme-version-agnostic fallback (variant changes are rare, so 400ms is negligible).
    let lastSeenVid = currentVariantId();
    const checkVid = () => { const v = currentVariantId(); if (String(v) !== String(lastSeenVid)) { lastSeenVid = v; onVariantMaybeChanged(); } };
    ["variant:update", "variant:change", "product:select", "on:variant:change", "variantChange"]
      .forEach((ev) => document.addEventListener(ev, checkVid, true));
    if (formIdInput) { document.addEventListener("change", checkVid, true); setInterval(checkVid, 400); }
  }

  function boot() {
    document.querySelectorAll("[data-perso]").forEach(init);
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
})();
